import React, { useContext, useState, useEffect, useRef } from 'react';
import { LoadingOutlined, CheckCircleFilled, CloseOutlined, DownOutlined, UpOutlined, FileOutlined, ExclamationCircleFilled, DownloadOutlined } from '@ant-design/icons';
import BulkTaskContext from './ContextFiles/BulkTaskContext';

const BulkTaskPanel = () => {
  const { tasks, removeTask, clearCompleted } = useContext(BulkTaskContext);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const prevTaskCountRef = useRef(0);

  // Auto-show panel when new tasks are added after dismissal
  useEffect(() => {
    if (tasks.length > prevTaskCountRef.current && dismissed) {
      setDismissed(false);
    }
    prevTaskCountRef.current = tasks.length;
  }, [tasks.length, dismissed]);

  // Re-show panel when a task completes or fails while dismissed
  // After dismiss, clearCompleted() removes finished tasks — only in_progress remain.
  // So any completed/failed task here means it just finished.
  useEffect(() => {
    if (dismissed && tasks.some((t) => t.status === 'completed' || t.status === 'failed')) {
      setDismissed(false);
    }
  }, [tasks, dismissed]);

  // Nothing to show
  if (tasks.length === 0 || dismissed) return null;

  const inProgressCount = tasks.filter((t) => t.status === 'in_progress').length;
  const headerTitle = inProgressCount > 0
    ? `Preparing download${inProgressCount > 1 ? 's' : ''}`
    : 'Downloads ready';

  const handleDismiss = () => {
    clearCompleted();
    setDismissed(true);
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      width: '360px',
      background: '#fff',
      borderRadius: '8px',
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12), 0 3px 6px rgba(0, 0, 0, 0.08)',
      zIndex: 1050,
      overflow: 'hidden',
      fontFamily: 'Manrope, -apple-system, sans-serif',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: collapsed ? 'none' : '1px solid #f0f0f0',
      }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: '#262626' }}>
          {headerTitle}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            onClick={() => setCollapsed(!collapsed)}
            style={{ cursor: 'pointer', color: '#8c8c8c', fontSize: '14px', display: 'flex', alignItems: 'center' }}
          >
            {collapsed ? <DownOutlined /> : <UpOutlined />}
          </span>
          <span
            onClick={handleDismiss}
            style={{ cursor: 'pointer', color: '#8c8c8c', fontSize: '14px', display: 'flex', alignItems: 'center' }}
          >
            <CloseOutlined />
          </span>
        </div>
      </div>

      {/* Task list */}
      {!collapsed && (
        <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
          {tasks.map((task) => (
            <div
              key={task.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 16px',
                gap: '12px',
                borderBottom: '1px solid #fafafa',
              }}
            >
              {/* File icon */}
              <FileOutlined style={{ fontSize: '18px', color: '#8c8c8c', flexShrink: 0 }} />

              {/* Label + progress */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '13px',
                  color: '#262626',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {task.label || `Zipping ${task.imageCount} file${task.imageCount !== 1 ? 's' : ''}`}
                </div>
                {task.status === 'in_progress' && task.processedItems != null && task.totalItems > 0 && (
                  <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}>
                    {task.processedItems} / {task.totalItems} processed
                  </div>
                )}
              </div>

              {/* Status indicator / download button */}
              <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                {task.status === 'in_progress' && (
                  <LoadingOutlined style={{ fontSize: '18px', color: '#1890ff' }} spin />
                )}
                {task.status === 'completed' && task.downloadUrl && (
                  <a
                    href={task.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Download ZIP"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <DownloadOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                  </a>
                )}
                {task.status === 'completed' && (
                  <CheckCircleFilled style={{ fontSize: '18px', color: '#52c41a' }} />
                )}
                {task.status === 'failed' && (
                  <ExclamationCircleFilled style={{ fontSize: '18px', color: '#ff4d4f' }} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BulkTaskPanel;
